export const DEFAULT_ACCENT = "#0fa3a3";

const HEX_COLOR = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

export function accentColor(primaryColor: string | undefined): string {
  const value = primaryColor?.trim();
  return value && HEX_COLOR.test(value) ? value : DEFAULT_ACCENT;
}

export function accentInk(hex: string): string {
  const digits = hex.slice(1);
  const full = digits.length === 3 ? digits.split("").map((d) => d + d).join("") : digits;
  const channel = (offset: number) => {
    const c = parseInt(full.slice(offset, offset + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
  return luminance > 0.18 ? "#04121a" : "#ffffff";
}

export type ChatAppearance = "dark" | "light" | "auto";

export function rootClassName(
  position: "bottom-right" | "bottom-left" | undefined,
  appearance: ChatAppearance | undefined,
): string {
  const classes = ["kb-root"];
  if (position === "bottom-left") classes.push("kb-left");
  if (appearance === "light") classes.push("kb-light");
  if (appearance === "auto") classes.push("kb-auto");
  return classes.join(" ");
}

export const CHAT_FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Archivo:wght@500&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@500&display=swap";

const CHAT_FONTS_LINK_ID = "kanha-ai-fonts";

export function loadChatFonts(): void {
  if (typeof document === "undefined" || document.getElementById(CHAT_FONTS_LINK_ID)) return;
  const link = document.createElement("link");
  link.id = CHAT_FONTS_LINK_ID;
  link.rel = "stylesheet";
  link.href = CHAT_FONTS_URL;
  document.head.appendChild(link);
}

export function themeVariables(primaryColor: string | undefined): Record<string, string> {
  const accent = accentColor(primaryColor);
  return { "--kb-accent": accent, "--kb-accent-ink": accentInk(accent) };
}

const LIGHT_VARIABLES = `
  --kb-text: #0b1f26;
  --kb-body: rgba(11, 31, 38, 0.76);
  --kb-faint: rgba(11, 31, 38, 0.56);
  --kb-edge: rgba(11, 31, 38, 0.12);
  --kb-edge-soft: rgba(11, 31, 38, 0.08);
  --kb-edge-strong: rgba(11, 31, 38, 0.28);
  --kb-danger: #b91c1c;
  --kb-solid: #ffffff;
  --kb-panel-fill: rgba(255, 255, 255, 0.97);
  --kb-panel-sheen: rgba(255, 255, 255, 0);
  --kb-rim-high: rgba(11, 31, 38, 0.16);
  --kb-rim-low: rgba(11, 31, 38, 0.06);
  --kb-glow: color-mix(in srgb, var(--kb-accent) 10%, transparent);
  --kb-shadow: rgba(4, 18, 26, 0.18);
  --kb-hover: rgba(11, 31, 38, 0.06);
  --kb-track: rgba(11, 31, 38, 0.08);
  --kb-scrollbar: rgba(11, 31, 38, 0.2);
  --kb-bubble: rgba(11, 31, 38, 0.06);
  --kb-code: rgba(11, 31, 38, 0.07);
  --kb-pre: rgba(11, 31, 38, 0.04);
  --kb-field: rgba(11, 31, 38, 0.05);
  color-scheme: light;
`;

export const CHAT_CSS = `
.kb-root {
  --kb-text: #eef4f4;
  --kb-body: rgba(238, 244, 244, 0.72);
  --kb-faint: rgba(238, 244, 244, 0.5);
  --kb-edge: rgba(255, 255, 255, 0.1);
  --kb-edge-soft: rgba(255, 255, 255, 0.07);
  --kb-edge-strong: rgba(255, 255, 255, 0.25);
  --kb-danger: #fca5a5;
  --kb-solid: #04121a;
  --kb-panel-fill: rgba(4, 18, 26, 0.97);
  --kb-panel-sheen: rgba(255, 255, 255, 0.035);
  --kb-rim-high: rgba(255, 255, 255, 0.26);
  --kb-rim-low: rgba(255, 255, 255, 0.05);
  --kb-glow: color-mix(in srgb, var(--kb-accent) 16%, transparent);
  --kb-shadow: rgba(0, 0, 0, 0.6);
  --kb-hover: rgba(255, 255, 255, 0.1);
  --kb-track: rgba(255, 255, 255, 0.06);
  --kb-scrollbar: rgba(255, 255, 255, 0.15);
  --kb-bubble: rgba(255, 255, 255, 0.08);
  --kb-code: rgba(255, 255, 255, 0.08);
  --kb-pre: rgba(0, 0, 0, 0.3);
  --kb-field: rgba(255, 255, 255, 0.05);
  --kb-sans: Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  --kb-display: Archivo, var(--kb-sans);
  --kb-mono: "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  color-scheme: dark;
  font-family: var(--kb-sans);
  font-size: 16px;
  line-height: 1.5;
  color: var(--kb-text);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
.kb-root.kb-light {${LIGHT_VARIABLES}}
@media (prefers-color-scheme: light) {
  .kb-root.kb-auto {${LIGHT_VARIABLES}}
}
:where(.kb-root) *, :where(.kb-root) *::before, :where(.kb-root) *::after { box-sizing: border-box; }
:where(.kb-root) :where(button, textarea) { font: inherit; letter-spacing: normal; text-transform: none; margin: 0; -webkit-tap-highlight-color: transparent; }
:where(.kb-root) :where(p) { margin: 0; }
:where(.kb-root) :where(svg) { display: block; flex-shrink: 0; }
@keyframes kb-spin { to { transform: rotate(360deg); } }
@keyframes kb-bounce { 0%, 80%, 100% { transform: translateY(0); } 40% { transform: translateY(-4px); } }
@keyframes kb-pulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); } 50% { box-shadow: 0 0 0 6px rgba(239, 68, 68, 0); } }
@keyframes kb-rise { from { opacity: 0; transform: translateY(12px) scale(0.98); } to { opacity: 1; transform: none; } }
.kb-fab {
  position: fixed; bottom: 24px; right: 24px; width: 56px; height: 56px; border-radius: 50%;
  background: var(--kb-accent); color: var(--kb-accent-ink); border: none; cursor: pointer;
  display: flex; align-items: center; justify-content: center; z-index: 2147483000;
  box-shadow: 0 10px 30px -8px color-mix(in srgb, var(--kb-accent) 70%, transparent), 0 4px 12px rgba(4, 18, 26, 0.35);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.kb-fab:hover { transform: translateY(-2px); }
.kb-fab:focus-visible { outline: 2px solid var(--kb-accent); outline-offset: 3px; }
.kb-left .kb-fab, .kb-left .kb-panel { right: auto; left: 24px; }
.kb-panel {
  position: fixed; bottom: 96px; right: 24px; width: 400px; max-width: calc(100vw - 48px);
  height: min(640px, calc(100vh - 120px)); height: min(640px, calc(100dvh - 120px));
  display: flex; flex-direction: column; overflow: hidden; z-index: 2147483000;
  border: 1px solid transparent; border-radius: 24px;
  background-image:
    radial-gradient(120% 60% at 50% -10%, var(--kb-glow), transparent 70%),
    linear-gradient(var(--kb-panel-sheen), var(--kb-panel-sheen)),
    linear-gradient(var(--kb-panel-fill), var(--kb-panel-fill)),
    linear-gradient(160deg, var(--kb-rim-high), var(--kb-edge) 42%, var(--kb-rim-low) 80%);
  background-origin: border-box;
  background-clip: padding-box, padding-box, padding-box, border-box;
  -webkit-backdrop-filter: blur(24px) saturate(140%);
  backdrop-filter: blur(24px) saturate(140%);
  box-shadow: 0 30px 80px -24px var(--kb-shadow), 0 24px 60px -30px color-mix(in srgb, var(--kb-accent) 45%, transparent);
}
.kb-panel-enter { animation: kb-rise 0.22s cubic-bezier(0.2, 0.8, 0.2, 1); }
.kb-header { display: flex; align-items: center; gap: 12px; padding: 16px 20px; border-bottom: 1px solid var(--kb-edge-soft); flex-shrink: 0; }
.kb-avatar {
  width: 32px; height: 32px; border-radius: 10px; flex-shrink: 0; display: flex; align-items: center; justify-content: center;
  background: color-mix(in srgb, var(--kb-accent) 18%, transparent); color: var(--kb-accent);
  border: 1px solid color-mix(in srgb, var(--kb-accent) 35%, transparent);
}
.kb-header-text { min-width: 0; flex: 1; }
.kb-header-title { font-size: 14.4px; color: var(--kb-text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.kb-header-sub { margin-top: 2px; font-size: 11.2px; color: var(--kb-faint); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.kb-header-actions { display: flex; align-items: center; gap: 4px; flex-shrink: 0; }
.kb-icon-btn {
  background: none; border: none; color: var(--kb-body); cursor: pointer; padding: 6px; border-radius: 8px;
  display: flex; align-items: center; justify-content: center; transition: background-color 0.15s ease, color 0.15s ease;
}
.kb-icon-btn:hover { background: var(--kb-hover); color: var(--kb-text); }
.kb-icon-btn:focus-visible { outline: 2px solid var(--kb-accent); outline-offset: 1px; }
.kb-progress { height: 2px; background: var(--kb-track); flex-shrink: 0; overflow: hidden; }
.kb-progress-fill { height: 100%; background: var(--kb-accent); transition: width 0.3s ease; }
.kb-messages { flex: 1; min-height: 0; overflow-y: auto; overscroll-behavior: contain; scrollbar-width: thin; scrollbar-color: var(--kb-scrollbar) transparent; }
.kb-thread { display: flex; flex-direction: column; gap: 20px; padding: 24px 20px; }
.kb-empty { height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; padding: 32px 24px; gap: 8px; color: var(--kb-body); font-size: 14.4px; }
.kb-empty-mark {
  width: 48px; height: 48px; border-radius: 14px; margin-bottom: 12px; display: flex; align-items: center; justify-content: center;
  background: color-mix(in srgb, var(--kb-accent) 16%, transparent); color: var(--kb-accent);
  border: 1px solid color-mix(in srgb, var(--kb-accent) 30%, transparent);
}
.kb-empty-title { font-family: var(--kb-display); font-size: 22px; font-weight: 500; letter-spacing: -0.02em; line-height: 1.15; color: var(--kb-text); }
.kb-empty-hint { font-size: 12px; color: var(--kb-faint); }
.kb-empty-error { color: var(--kb-danger); }
.kb-user { display: flex; justify-content: flex-end; }
.kb-user-bubble {
  max-width: 85%; padding: 10px 16px; border-radius: 18px 18px 6px 18px; background: var(--kb-bubble);
  color: var(--kb-text); font-size: 14.7px; line-height: 1.6; white-space: pre-wrap; overflow-wrap: anywhere;
}
.kb-bot { max-width: 92%; color: var(--kb-body); font-size: 14.7px; line-height: 1.6; overflow-wrap: anywhere; }
.kb-md > * + * { margin-top: 8px; }
.kb-md p, .kb-md ul, .kb-md ol, .kb-md pre, .kb-md h1, .kb-md h2, .kb-md h3, .kb-md h4, .kb-md blockquote { margin: 0; }
.kb-md * + p, .kb-md * + ul, .kb-md * + ol, .kb-md * + pre, .kb-md * + blockquote, .kb-md * + h1, .kb-md * + h2, .kb-md * + h3, .kb-md * + h4 { margin-top: 8px; }
.kb-md h1, .kb-md h2, .kb-md h3, .kb-md h4 { font-size: 1em; font-weight: 600; color: var(--kb-text); }
.kb-md strong { font-weight: 600; color: var(--kb-text); }
.kb-md ul, .kb-md ol { padding-left: 1.3em; }
.kb-md li + li { margin-top: 4px; }
.kb-md li::marker { color: var(--kb-faint); }
.kb-md a { color: var(--kb-text); text-decoration: underline; text-decoration-color: color-mix(in srgb, var(--kb-accent) 70%, transparent); text-underline-offset: 3px; }
.kb-md code { font-family: var(--kb-mono); font-size: 0.86em; background: var(--kb-code); padding: 1px 5px; border-radius: 5px; color: var(--kb-text); }
.kb-md pre { background: var(--kb-pre); border: 1px solid var(--kb-edge-soft); border-radius: 12px; padding: 10px 12px; overflow-x: auto; }
.kb-md pre code { background: none; padding: 0; font-size: 12.5px; line-height: 1.55; white-space: pre; }
.kb-md blockquote { border-left: 2px solid var(--kb-edge); padding-left: 12px; color: var(--kb-faint); }
.kb-sources { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-top: 12px; list-style: none; padding: 0; }
.kb-sources-label { font-family: var(--kb-mono); font-size: 9px; font-weight: 500; letter-spacing: 0.06em; text-transform: uppercase; color: var(--kb-faint); }
.kb-source {
  display: inline-block; max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; vertical-align: middle;
  font-family: var(--kb-mono); font-size: 11px; line-height: 1.4; color: var(--kb-body); text-decoration: none;
  border: 1px solid var(--kb-edge); border-radius: 999px; padding: 4px 10px; transition: border-color 0.15s ease, color 0.15s ease;
}
.kb-source:hover { border-color: var(--kb-edge-strong); color: var(--kb-text); }
.kb-source:focus-visible { outline: 2px solid var(--kb-accent); outline-offset: 2px; }
.kb-actions { display: flex; align-items: center; gap: 2px; margin: 8px 0 0 -6px; }
.kb-action {
  background: none; border: none; color: var(--kb-faint); cursor: pointer; padding: 6px; border-radius: 8px; min-height: 28px;
  display: inline-flex; align-items: center; gap: 6px; font-size: 11.5px; line-height: 1; transition: background-color 0.15s ease, color 0.15s ease;
}
.kb-action:hover { background: var(--kb-hover); color: var(--kb-text); }
.kb-action:focus-visible { outline: 2px solid var(--kb-accent); outline-offset: 1px; }
.kb-action-active, .kb-action-active:hover { color: var(--kb-accent); }
.kb-thinking { display: flex; align-items: center; gap: 8px; }
.kb-dots { display: flex; gap: 4px; }
.kb-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--kb-faint); animation: kb-bounce 1.2s ease-in-out infinite; }
.kb-dot:nth-child(2) { animation-delay: 150ms; }
.kb-dot:nth-child(3) { animation-delay: 300ms; }
.kb-thinking-label { font-size: 11.5px; color: var(--kb-faint); }
.kb-spinner { animation: kb-spin 1s linear infinite; }
.kb-suggestions { display: flex; flex-wrap: wrap; gap: 8px; padding: 14px 20px; border-top: 1px solid var(--kb-edge-soft); flex-shrink: 0; }
.kb-suggestion {
  border: 1px solid var(--kb-edge); background: none; color: var(--kb-body); border-radius: 999px; padding: 6px 12px;
  font-size: 12.5px; line-height: 1.4; text-align: left; cursor: pointer; transition: border-color 0.15s ease, color 0.15s ease;
}
.kb-suggestion:hover:not(:disabled) { border-color: var(--kb-edge-strong); color: var(--kb-text); }
.kb-suggestion:disabled { opacity: 0.45; cursor: not-allowed; }
.kb-suggestion:focus-visible { outline: 2px solid var(--kb-accent); outline-offset: 2px; }
.kb-status { padding: 10px 20px; border-top: 1px solid var(--kb-edge-soft); font-size: 11.5px; color: var(--kb-faint); flex-shrink: 0; display: flex; align-items: center; gap: 6px; }
.kb-status-error { color: var(--kb-danger); }
.kb-composer { display: flex; align-items: flex-end; gap: 10px; padding: 14px 16px; border-top: 1px solid var(--kb-edge-soft); flex-shrink: 0; }
.kb-textarea {
  flex: 1; min-width: 0; min-height: 42px; max-height: 120px; resize: none; overflow-y: auto;
  padding: 10px 14px; border: none; border-radius: 12px; background: var(--kb-field); color: var(--kb-text);
  font-size: 14.4px; line-height: 1.5; outline: none;
}
.kb-textarea::placeholder { color: var(--kb-faint); opacity: 1; }
.kb-textarea:focus-visible { box-shadow: 0 0 0 2px var(--kb-accent); }
.kb-textarea:disabled { cursor: not-allowed; }
.kb-send {
  flex-shrink: 0; height: 42px; min-width: 60px; padding: 0 16px; border: none; border-radius: 12px;
  background: var(--kb-accent); color: var(--kb-accent-ink); font-size: 13px; font-weight: 500; cursor: pointer;
  display: flex; align-items: center; justify-content: center; transition: opacity 0.15s ease;
}
.kb-send:hover:not(:disabled) { opacity: 0.9; }
.kb-send:disabled { opacity: 0.45; cursor: not-allowed; }
.kb-send:focus-visible { outline: 2px solid var(--kb-accent); outline-offset: 2px; }
.kb-send-stop { background: #ef4444; color: #04121a; }
.kb-mic {
  flex-shrink: 0; width: 42px; height: 42px; border-radius: 12px; border: 1px solid var(--kb-edge); background: none; color: var(--kb-body);
  cursor: pointer; display: flex; align-items: center; justify-content: center; transition: border-color 0.15s ease, color 0.15s ease;
}
.kb-mic:not(.kb-mic-active):not(:disabled):hover { border-color: var(--kb-edge-strong); color: var(--kb-text); }
.kb-mic:disabled { opacity: 0.45; cursor: not-allowed; }
.kb-mic:focus-visible { outline: 2px solid var(--kb-accent); outline-offset: 2px; }
.kb-mic-active { background: #ef4444; border-color: #ef4444; color: #fff; animation: kb-pulse 1.4s ease-in-out infinite; }
.kb-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
@media (max-width: 640px) {
  .kb-panel, .kb-left .kb-panel {
    inset: 0; width: 100%; max-width: none; height: 100%; border-radius: 0; border: none;
    padding-top: env(safe-area-inset-top); padding-bottom: env(safe-area-inset-bottom);
    padding-left: env(safe-area-inset-left); padding-right: env(safe-area-inset-right);
    background-image: radial-gradient(120% 45% at 50% -10%, var(--kb-glow), transparent 70%), linear-gradient(var(--kb-solid), var(--kb-solid));
    background-clip: padding-box;
  }
  .kb-fab-open { display: none; }
  .kb-fab { bottom: calc(16px + env(safe-area-inset-bottom)); }
  .kb-textarea { font-size: 16px; }
  .kb-bot, .kb-user-bubble { font-size: 15px; }
}
@media (prefers-reduced-motion: reduce) {
  .kb-panel-enter, .kb-dot, .kb-spinner, .kb-mic-active { animation: none; }
  .kb-fab, .kb-fab:hover { transition: none; transform: none; }
}
`;
